import React, { useContext } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { AugmentedColumn, CaptionProp } from "../../../@types/react-table";
import { Routes } from "../../../app";
import { RenderedTable } from "../../../components";
import { PlanDetailsPageSection, PrescriptionDrug } from "../../../@types";
import { useTableConfig, useTranslate, CONSTANTS } from "../../../helpers";

import {
  displayDrug,
  displayPackage,
  hasDuplicatePrescriptions,
} from "../../../helpers/prescriptionHelpers";
import { DuplicatePrescriptionNotification } from "../../../components/DuplicatePrescriptionNotification";
import { AnalyticsActionType } from "../../../app/contexts/Analytics/types";
import { AppContext } from "../../../app/store";

export const MyDrugListTable: React.FunctionComponent<{
  prescriptions: PrescriptionDrug[];
}> = ({ prescriptions }) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const location = useLocation();
  const returnLocation = location;
  returnLocation.hash = PlanDetailsPageSection.DRUG_COVERAGE;

  const colSource: Array<AugmentedColumn> = [
    {
      accessor: "drug",
    },
    {
      accessor: "package",
      Header: t("prescription.package"),
    },
    {
      accessor: "quantity",
      Header: t("prescription.quantity"),
    },
    {
      accessor: "frequency",
      Header: t("prescription.frequency"),
    },
    {
      accessor: "generic_or_brand",
      Header: t("plan_terms.drug_list.brand_or_generic"),
    },
  ];

  let cols = colSource;

  if (hasDuplicatePrescriptions(prescriptions)) {
    cols = [
      {
        accessor: "duplicate_drug_heading",
        Header: (
          <DuplicatePrescriptionNotification linkPrescriptionList={true} />
        ),
        columns: colSource,
        attributes: {
          alert: true,
        },
      },
    ];
  }

  const data = prescriptions.map(prescription => {
    return {
      drug: displayDrug(t, prescription),
      package: prescription.use_package_quantity
        ? displayPackage(t, prescription)
        : "",
      quantity: prescription.quantity,
      frequency: t(
        CONSTANTS.prescriptionFrequencyLabels[prescription.frequency]
      ),
      generic_or_brand: prescription.is_generic
        ? t("plan_terms.drug_list.generic")
        : t("plan_terms.drug_list.brand"),
    };
  });

  const caption: CaptionProp = {
    title: t("plan_terms.my_drug_list"),
    render: function renderTableCaption(title: JSX.Element) {
      return (
        <div className="ds-u-display--flex ds-u-flex-wrap--wrap ds-u-justify-content--between ds-u-align-items--center mct-u-width--full">
          {title}
          <Link
            className="ds-c-button mct-u-width--full mct-u-sm-width--auto mct-hide-print"
            to={{
              pathname: Routes.prescriptionsList,
              state: { nextRoute: returnLocation },
            }}
            onClick={() => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Plan Details",
                  event_label: "Edit/Remove drugs",
                },
              });
            }}
          >
            {t("plan_terms.drug_list.change_drugs")}
          </Link>
        </div>
      );
    },
  };

  return (
    <RenderedTable
      tableColumns={useTableConfig(cols, [t, prescriptions])}
      tableData={React.useMemo(() => data, [t])}
      rowHeaders // Indicates the first column should be rendered as `th scope="row"`
      borderless // is a default for Plan Details, TBD if it should be baked in
      stackableBreakpoint="lg" // is a default for Plan Details, TBD if it should be baked in
      caption={caption}
    />
  );
};
